import {Box, Skeleton, Stack, Typography, useTheme} from '@mui/material';
import React from 'react';

import {Carousel} from 'presentation/components';
import {MyAppointmentsEnum} from 'presentation/constants';
import {CarrouselTypeEnum} from 'presentation/constants/carrouselTypeEnum';
import {useContractStatus} from 'presentation/hooks';
import {useAccountInformation, useMediaQueries} from 'presentation/pages/private';

import {AppointmentCard} from 'presentation/modules/myAppointments/ui/components/AppointmentCard';
import {translations} from 'presentation/translations-new';
import {useNavigate} from 'react-router-dom';
import {useGetHomeQuery} from '../../query/useGetHomeQuery';
import {LoadErrorOrEmptyAlert} from '../LoadErrorOrEmptyAlert';
import {SectionContainer} from '../SectionContainer';
import {SKELETON_HEIGHT} from './constants';
import {EmptyStateDashedBox, NextAppointmentsContainer, StyledBox} from './styles';

const {loadError, seeAll} = translations['pt-br'].commons;
const {nextAppointments, nextAppointmentsLoadError, nextAppointmentsEmpty} =
	translations['pt-br'].privateFlow.home;

export function NextAppointments() {
	const {isLaptop} = useMediaQueries();
	const {palette} = useTheme();
	const navigate = useNavigate();

	const {data: appointmentsData, isError, isLoading} = useGetHomeQuery();

	const {isResponsibleWithoutBenefits} = useAccountInformation();
	const {isValid} = useContractStatus();

	if (!isValid || isResponsibleWithoutBenefits) {
		return null;
	}

	const handleNavigation = (route: string) => {
		navigate(route);
	};

	if (isError) {
		return (
			<SectionContainer title={nextAppointments} titleMarginTop={0} showTitle>
				<NextAppointmentsContainer $noMargin>
					<LoadErrorOrEmptyAlert
						type="error"
						title={loadError}
						description={nextAppointmentsLoadError}
					/>
				</NextAppointmentsContainer>
			</SectionContainer>
		);
	}

	if (isLoading) {
		return (
			<SectionContainer title={nextAppointments} titleMarginTop={0} showTitle>
				<NextAppointmentsContainer $noMargin>
					{isLaptop ? (
						<Box>
							<Skeleton sx={{transform: 'initial'}} height={72} />
						</Box>
					) : (
						<Stack direction="row" spacing={3}>
							<Box flexBasis="40%">
								<Skeleton sx={{transform: 'initial'}} height={SKELETON_HEIGHT} />
							</Box>
							<Box flexBasis="40%">
								<Skeleton sx={{transform: 'initial'}} height={SKELETON_HEIGHT} />
							</Box>
							<Box flexBasis="20%">
								<Skeleton sx={{transform: 'initial'}} height={SKELETON_HEIGHT} />
							</Box>
						</Stack>
					)}
				</NextAppointmentsContainer>
			</SectionContainer>
		);
	}

	const isEmpty = !appointmentsData?.appointments || appointmentsData?.appointments?.length === 0;
	const isSingleItem = appointmentsData?.appointments?.length === 1;

	if (isEmpty) {
		return (
			<SectionContainer title={nextAppointments} titleMarginTop={0} showTitle>
				<NextAppointmentsContainer $noMargin>
					<EmptyStateDashedBox>
						<Typography variant="body2" color={palette.text.primary}>
							{nextAppointmentsEmpty}
						</Typography>
					</EmptyStateDashedBox>
				</NextAppointmentsContainer>
			</SectionContainer>
		);
	}

	return (
		<SectionContainer
			title={nextAppointments}
			actionText={seeAll}
			showTitle
			titleMarginTop={0}
			titleMarginBottom={isLaptop ? 2 : 1}
			handleAction={() => handleNavigation(MyAppointmentsEnum.MY_APPOINTMENTS)}>
			<NextAppointmentsContainer>
				<StyledBox $isSingleItem={isSingleItem}>
					<Carousel
						type={CarrouselTypeEnum.ARROWS_DOTS}
						options={{dragFree: false, containScroll: 'trimSnaps'}}
						slides={appointmentsData?.appointments?.map(
							(appointment, index: number) => (
								<Box padding={isLaptop ? 0.5 : 1} height="100%" key={index}>
									<AppointmentCard
										appointment={appointment}
										hasFavoriteButton
										isFavoriteButtonDisabled
									/>
								</Box>
							),
						)}
					/>
				</StyledBox>
			</NextAppointmentsContainer>
		</SectionContainer>
	);
}
